import { useEffect, useRef } from "react"
import posthog from "posthog-js"

import { APPLICATION_FORM_VERSION } from "@/lib/application-form-version"
import { PAYMENT_RETURN_EVENT } from "@/lib/analytics/payment-return"
import { forwardAnalyticsEventToMetaPixel } from "@/lib/meta-pixel"
import { acquisitionChannelFrom, readAcquisitionContext } from "@/lib/acquisition/context"
import { detectPaidSourceLabel } from "@/lib/leads/paid-source"
import { truncateText } from "@/lib/utils"
import {
  ANALYTICS_PERSON_COOKIE,
  normalizeAnalyticsPersonId,
  normalizePosthogSessionId,
  PH_SESSION_COOKIE,
} from "@/lib/leads/analytics-identity"

// Product-analytics event taxonomy — one home for every custom PostHog event
// name so the apply→call funnel stays queryable and we never ship a typo'd
// event string. posthog-js autocapture already records raw $pageview +
// $autocapture clicks; the events below are the SEMANTIC funnel milestones
// autocapture CAN'T infer: SPA step changes inside the application, the lead
// captured mid-form, the post-fetch success/booking states, and account
// creation. See context/analytics.md for the funnel map + the PostHog-native
// auto-feedback options (insight subscriptions, alerts, cohort export).

/** Pin every apply funnel query to one form shape. A change to the step ids or
 * the step order changes every step_number and every funnel total, so a query
 * that spans two versions is comparing two different forms and will read as a
 * drop that never happened.
 *
 * It is the SAME stamp the unified form writes onto every row it saves
 * (`answers.form_version`, web/lib/application-form-version.ts), so an event
 * and the row it produced always name the same form. The version before the
 * unified form was "2026-08-22", the two-arm money experiment.
 *
 * IT IS A VERSION STAMP, NOT A DATE, and PostHog could not tell. It inferred
 * DateTime from the date-shaped value and wrapped every read in
 * parseDateTime64BestEffortOrNull, which made `contains` — an operator the UI
 * offers — throw instead of returning rows. Worse, because the coercion is the
 * OrNull variant, the first bump to anything a date parser rejects ("v3",
 * "2026-08-22b") would have turned the property NULL on every event, and every
 * apply funnel pins this property, so the whole funnel would have read zero
 * with no error anywhere. The property definition is now explicitly typed
 * String in PostHog, so a non-date value is safe. If you ever add another
 * version-stamp property, type it String there before the first event lands. */
export const APPLY_FORM_VERSION = APPLICATION_FORM_VERSION

export const AnalyticsEvent = {
  // Apply funnel (the business goal: visitor → application → booked call)
  /** First genuine input (keystroke or option tap), once per visit. */
  ApplyStarted: "apply_started",
  /** Visitor reached step N (forward, back, or initial step on mount). */
  ApplyStepViewed: "apply_step_viewed",
  /** Continue / submit control clicked. blocked=true when validation failed. */
  ApplyContinueClicked: "apply_continue_clicked",
  /** Required/format/reachability failure surfaced on Continue. */
  ApplyValidationError: "apply_validation_error",
  /** The visible /apply availability request failed; diagnostic, never a conversion. */
  ApplySlotsLoadFailed: "apply_slots_load_failed",
  ApplyLeadCaptured: "apply_lead_captured",
  ApplySubmitted: "apply_submitted",
  // There is no separate "booking calendar shown" event. Booking is the LAST
  // STEP of the form, so `apply_step_viewed` with `step_id='booking'` already
  // marks the moment the calendar renders. The old `apply_booking_shown` name
  // outlived the post-submit calendar it was written for and had no emitter,
  // so every consumer of it read a guaranteed zero.
  /** Mid-form visitor shows leave intent (mouse to browser bar / tab hidden).
   * Fired once per visit after ApplyStarted. Analytics only: the
   * abandon-reason sheet that once rode this trigger was removed by owner
   * order ("annoying"); historical apply_abandon_reason events remain in
   * PostHog but nothing emits them any more. */
  ApplyExitIntent: "apply_exit_intent",
  /** The Cal.com embed on /book fired bookingSuccessful and the prospect
   * landed on our own branded /what-happens-next pre-frame page (rather than
   * Cal's generic confirmation screen). A page view, not the conversion: the
   * conversion is `booking_confirmed` below, fired server-side by the Cal
   * webhook when the booking is actually recorded. */
  BookingConfirmedViewed: "booking_confirmed_viewed",
  // THE PROSPECT PIPELINE'S CANONICAL VOCABULARY: answer completion is
  // progress, booking confirmation is submission. Five names, exactly these,
  // and a calendar-reached prospect is NEVER reported as a submitted
  // application. `apply_lead_captured` and `apply_submitted` above are the
  // older names the /apply client still emits and the HogQL readers still count; their
  // emitters and readers migrate onto these names, and a query that spans the
  // rename is comparing two vocabularies.
  /** Valid name and email saved, through ANY door: /apply's contact step, the
   * newsletter bubble, the first question set. The newsletter subscriber and
   * the lead are the same person. */
  LeadCaptured: "lead_captured",
  /** Proceeding beyond contact capture: the first step after the contact step
   * is reached. Distinct from `apply_started` (the first keystroke, before any
   * contact exists). */
  ApplicationStarted: "application_started",
  /** The inline booking screen rendered, every required question answered.
   * PROGRESS, an optional derived substate of an in-progress application, and
   * explicitly not a submission. */
  CalendarReached: "calendar_reached",
  /** Cal.com confirmed the booking and the lead row was stamped. THE canonical
   * conversion, fired server-side only (web/app/api/webhooks/cal/route.ts),
   * once per booking uid, with `first_submission` true when it set the man's
   * submitted_at. */
  BookingConfirmed: "booking_confirmed",
  /** The same canonical conversion under its other name. Submitting IS booking
   * confirmed, so this is deliberately the same event string and never a second
   * event; a funnel counts it once. */
  ApplicationSubmitted: "booking_confirmed",
  // Checkout
  /** /pay/return learned from the status route how the payment ended:
   * `payment_state` (succeeded, processing, failed), `intent_kind` (payment,
   * or setup for a weekly plan that saved his card and charged nothing) and
   * `plan_id` when the offer is a catalog plan. The nightly replay report reads
   * it; nothing fulfils on it. Name and properties:
   * web/lib/analytics/payment-return.ts. */
  PaymentReturnViewed: PAYMENT_RETURN_EVENT,
  // Acquisition
  /** The intro-course popup (modal or mobile-article banner) became visible.
   * Without this, view-to-signup rate and dismissal-linked exits are
   * unmeasurable and no placement ruling can ever be judged. */
  IntroPopupShown: "intro_popup_shown",
  /** The visitor closed the intro-course popup without giving an email. */
  IntroPopupDismissed: "intro_popup_dismissed",
  NewsletterSignup: "newsletter_signup",
  AccountCreated: "account_created",
  // Situation pages (web/lib/situation-page-registry.ts). Each is an
  // acquisition context for an EXISTING product, so the whole point of the
  // events is attribution: which situation a man arrived from and what he did
  // next. Every one carries the shared situation props built by
  // situationEventProps() below; the helpers under "Situation pages" are the
  // only emitters. `application_started`, `booking_confirmed` and
  // `purchase_completed` already exist or belong to their own surfaces and are
  // not duplicated here.
  /** The page rendered. Fired once per mount by useSituationPageView(). */
  SituationPageViewed: "situation_page_viewed",
  /** He picked one of the page's first-question paths (the diagnostic router
   * or the "which describes you" cards). */
  SituationPathSelected: "situation_path_selected",
  /** He clicked a related situation, related article or related offer link. */
  RelatedGuideClicked: "related_guide_clicked",
  /** A recommended product card was shown to him. */
  RecommendedProductViewed: "recommended_product_viewed",
  /** He clicked a CTA on the page: apply, a product page, a checkout. */
  SituationCtaClicked: "situation_cta_clicked",
  // Article pages. There is deliberately no click twin: autocapture records the
  // click with its data-attr label (lib/articles/cta-measure.ts).
  /** An article's closing band, next-step rail or inline offer link first
   *  entered the viewport. Once per placement per page view. */
  CtaViewed: "cta_viewed",
  // /bootcamp. A named click per button (apply, pay, dates, any other), because
  // autocapture there cannot say which weekend or plan a click carried.
  /** A button on /bootcamp was clicked. Props: `cta`, `href`, `page_path`,
   *  and where they apply `plan_id`, `weekend`, `module`. Fired by
   *  app/(marketing)/bootcamp/_components/bootcamp-click-tracking.tsx. */
  BootcampCtaClicked: "bootcamp_cta_clicked",
} as const

export type AnalyticsEventName =
  (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent]

type AnalyticsProps = Record<
  string,
  string | number | boolean | null | undefined
>

/**
 * `acquisition_channel` on the two funnel events that fire in the browser,
 * lead_captured and application_started, read off the visit's envelope with the
 * SAME function the server runs to store the column (acquisitionChannelFrom), so
 * the event and the row agree. Omitted when the visit has no envelope or no
 * channel. The server-fired booking_confirmed stamps it from the stored row.
 */
function withAcquisitionChannel(event: AnalyticsEventName, props?: AnalyticsProps): AnalyticsProps | undefined {
  if (event !== AnalyticsEvent.LeadCaptured && event !== AnalyticsEvent.ApplicationStarted) return props
  const channel = acquisitionChannelFrom(readAcquisitionContext() ?? {})
  return channel ? { ...props, acquisition_channel: channel } : props
}

/**
 * Fire a product event. No-op on the server or before posthog-js initializes.
 * Analytics must never break the UI, so every call is wrapped defensively.
 */
export function track(event: AnalyticsEventName, props?: AnalyticsProps) {
  if (typeof window === "undefined") return
  try {
    posthog.capture(event, withAcquisitionChannel(event, props))
  } catch {
    // Swallow — a failed capture must never surface to the visitor.
  }
  try {
    // Meta Pixel piggybacks on the same taxonomy (inert until
    // NEXT_PUBLIC_META_PIXEL_ID is set): the funnel milestones Meta ads
    // optimize against are forwarded from this one choke point so no call
    // site ever fires the pixel directly. See web/lib/meta-pixel.ts.
    forwardAnalyticsEventToMetaPixel(event, props)
  } catch {
    // Swallow — ad measurement must never surface to the visitor either.
  }
}

/**
 * Shared props stamped on every apply funnel event.
 *
 * `money_arm` is on EVERY apply event on purpose, not just the money step. The
 * experiment asks whether disclosing a price changes who gets through, so the
 * question is what a man does at step one and at the calendar, not only what he
 * does on the screen that differs. Without it on every event the funnel cannot
 * be split by arm at all, which is the only reason the experiment is legible in
 * PostHog rather than in SQL alone.
 */
export function applyEventProps(
  program?: string | null,
  moneyArm?: string | null,
): AnalyticsProps {
  return {
    form_version: APPLY_FORM_VERSION,
    ...(program !== undefined ? { program: program ?? null } : {}),
    // Omitted rather than sent as null before hydration assigns one, so a
    // breakdown never grows a phantom "null" cohort next to the two real arms.
    ...(moneyArm ? { money_arm: moneyArm } : {}),
  }
}

/**
 * Tie this browser's analytics to a signed-in account and enrich the person
 * record so PostHog can segment + build cohorts (e.g. "applied, not yet
 * booked") and export them to feed the article pipeline. The owner directive
 * (2026-06-27) is to capture all first-party product data; email + name are the
 * join keys that make cohort export → AI feedback possible. Empty values are
 * dropped so we never overwrite a real property with a blank.
 */
export function identifyUser(
  id: string,
  props?: { email?: string | null; name?: string | null } & AnalyticsProps,
) {
  if (typeof window === "undefined" || !id) return
  try {
    const set: AnalyticsProps = {}
    if (props) {
      for (const [key, value] of Object.entries(props)) {
        if (value !== undefined && value !== null && value !== "") {
          set[key] = value
        }
      }
    }
    posthog.identify(id, Object.keys(set).length > 0 ? set : undefined)
  } catch {
    // Swallow — identify failure must never break sign-in.
  }
}

/**
 * Stamp a lead's email onto the CURRENT PostHog person the moment they hand it
 * over (intro popup, free-training gate, apply step 1), so funnel reports show
 * reachable people instead of anonymous devices (owner, 2026-07-03). This
 * deliberately does NOT call identify(email): account sign-in identifies by
 * Supabase user id, and PostHog never merges two already-identified persons,
 * so an email-identify here would permanently split the lead from their later
 * account. Person properties set pre-identify survive the merge instead.
 */
export function stitchLeadEmail(email: string | null | undefined) {
  if (typeof window === "undefined") return
  const normalized = (email ?? "").trim().toLowerCase()
  if (!normalized) return
  try {
    posthog.setPersonProperties({ email: normalized })
  } catch {
    // Swallow — analytics must never break a signup.
  }
}

// ---------------------------------------------------------------------------
// First-touch attribution (owner want: know how visitors found Cold Approach).
// PostHog captures referrer/UTM natively for analytics; this small helper
// additionally STAMPS the first touch onto each /apply lead so the source
// shows up on the stored row and the team notification email.
// ---------------------------------------------------------------------------

const FIRST_TOUCH_KEY = "ca_first_touch"

/** The post a tagged link came from: its `utm_content`, reduced to the id
 * characters our own links carry so it can never forge a " | " segment. Empty
 * for a visit with no tag. */
function utmContentId(params: URLSearchParams): string {
  return (params.get("utm_content") ?? "").replace(/[^A-Za-z0-9_.:~-]/g, "").slice(0, 60)
}

/** The first-touch summary for one landing: "paid: ... | utm: src / medium /
 * campaign | content: <post id> | ref: <domain> | landed: <path>", any part
 * optional. `content` is the per-post tag (utm_content) so a lead row names the
 * exact video it first arrived from. */
export function buildFirstTouchSummary(input: {
  search: string
  referrer: string
  hostname: string
  pathname: string
}): string {
  const params = new URLSearchParams(input.search)
  const utm = ["utm_source", "utm_medium", "utm_campaign"]
    .map((k) => params.get(k))
    .filter(Boolean)
    .join(" / ")
  const content = utmContentId(params)
  const ref = input.referrer && !input.referrer.includes(input.hostname)
    ? new URL(input.referrer).hostname
    : ""
  // A paid click id, when present, leads the summary. THIS is the path that
  // normally fills a lead's answers.source (the server cookie only fills in
  // when localStorage came back empty), so without it here the paid marker
  // would almost never reach a lead and cost per lead could never separate
  // paid from organic. Only the presence is recorded, never the token.
  const paid = detectPaidSourceLabel(params)
  const parts = [
    paid ?? "",
    utm ? `utm: ${utm}` : "",
    content ? `content: ${content}` : "",
    ref ? `ref: ${ref}` : "",
    `landed: ${input.pathname}`,
  ].filter(Boolean)
  return truncateText(parts.join(" | "), 300)
}

/** Record the visitor's first touch once per browser (called from
 * instrumentation-client on every page entry; only the first ever wins). */
export function captureFirstTouch(): void {
  if (typeof window === "undefined") return
  try {
    if (localStorage.getItem(FIRST_TOUCH_KEY)) return
    localStorage.setItem(
      FIRST_TOUCH_KEY,
      buildFirstTouchSummary({
        search: window.location.search,
        referrer: document.referrer,
        hostname: window.location.hostname,
        pathname: window.location.pathname,
      }),
    )
  } catch {
    // storage unavailable (private mode) — PostHog still has the analytics side
  }
}

/** The stored first-touch summary, or "direct" when nothing was recorded. */
export function firstTouchSummary(): string {
  if (typeof window === "undefined") return ""
  try {
    return localStorage.getItem(FIRST_TOUCH_KEY) || "direct / unknown"
  } catch {
    return ""
  }
}

/** Current PostHog distinct id for lead-draft attribution (best-effort). */
export function getPosthogDistinctId(): string | undefined {
  if (typeof window === "undefined") return undefined
  try {
    return posthog.get_distinct_id()
  } catch {
    return undefined
  }
}

/**
 * Current PostHog session id, the key that ties an event to this visit and to
 * its session replay.
 *
 * The distinct id says WHO. This says WHICH VISIT, and it is the half the
 * server never had: a booked call could be attributed to a person and never to
 * the browsing session that produced it, so nobody could watch what he did in
 * the minutes before he booked.
 */
export function getPosthogSessionId(): string | undefined {
  if (typeof window === "undefined") return undefined
  try {
    return normalizePosthogSessionId(posthog.get_session_id())
  } catch {
    return undefined
  }
}

/**
 * Publish the session id to the server as a first-party cookie.
 *
 * A COOKIE RATHER THAN A REQUEST FIELD, because the session id is a fact about
 * the browser rather than about one form, and a cookie reaches every route
 * without each one being taught to carry it. The apply route reads it, and any
 * later route that fires a server-side event can too.
 *
 * The max age matches PostHog's own idle session timeout, so the cookie expires
 * at roughly the moment the session it names does. A stale value would attach a
 * server event to a visit that had already ended.
 */
export function syncPosthogSessionCookie(): void {
  if (typeof window === "undefined") return
  try {
    const sessionId = getPosthogSessionId()
    if (!sessionId) return
    const secure = window.location.protocol === "https:" ? "; secure" : ""
    document.cookie = `${PH_SESSION_COOKIE}=${sessionId}; path=/; max-age=1800; samesite=lax${secure}`
  } catch {
    // Storage or cookies unavailable. Events still carry the distinct id.
  }
}

const PERSON_ADOPTED_KEY = "ca_person_adopted"

function readCookie(name: string): string | undefined {
  try {
    const prefix = `${name}=`
    for (const part of document.cookie.split(";")) {
      const entry = part.trim()
      if (entry.startsWith(prefix)) return entry.slice(prefix.length)
    }
  } catch {
    // Cookies unavailable.
  }
  return undefined
}

/**
 * Stamp the server-resolved analytics person id onto the PostHog person.
 *
 * The id is the canonical prospect's own id, adopted server-side onto the lead
 * row and handed back to the browser in a first-party cookie, so it survives
 * the tab that earned it and lands on the next page this man opens - and so two
 * application attempts by the same man carry the same id rather than two.
 *
 * IT IS DELIBERATELY NOT `posthog.identify(personId)`. Identifying would swap
 * this browser's distinct id for the person id, and PostHog never merges two
 * already-identified persons: the next real sign-in calls identify with the
 * Supabase user id and would be refused, permanently splitting the lead from
 * his own account. That is the same trap `stitchLeadEmail` exists to avoid. A
 * person property has no such failure mode and joins just as well.
 *
 * `$set_once`, because an identity that can be rewritten is not one. Guarded on
 * a stored marker so an ordinary page load does not fire a person update every
 * time.
 */
export function adoptAnalyticsPersonId(): void {
  if (typeof window === "undefined") return
  const personId = normalizeAnalyticsPersonId(readCookie(ANALYTICS_PERSON_COOKIE))
  if (!personId) return
  try {
    if (localStorage.getItem(PERSON_ADOPTED_KEY) === personId) return
  } catch {
    // Storage unavailable. Falling through re-sets the same value, which
    // $set_once makes a no-op on PostHog's side.
  }
  try {
    posthog.setPersonProperties(undefined, { analytics_person_id: personId })
  } catch {
    // Analytics is never allowed to break a page.
    return
  }
  try {
    localStorage.setItem(PERSON_ADOPTED_KEY, personId)
  } catch {
    // Unpersisted. Worst case is one redundant $set_once next load.
  }
}

// ---------------------------------------------------------------------------
// Synthetic-bot marker, one of the TWO analytics exclusions (the other is the
// owner/Matthew device opt-out below; every real visitor is always recorded).
// The automated weekly funnel-inspection (scripts/e2e/funnel-inspection.mjs)
// is a synthetic Playwright bot that runs the whole funnel, and counting its
// fake started->submitted->booked events would corrupt the (tiny) real funnel
// data. It self-marks with the key below via addInitScript before navigating.
// instrumentation-client reads this BEFORE deciding whether to init PostHog,
// so the bot sends zero calls.
// ---------------------------------------------------------------------------

const SYNTHETIC_BOT_KEY = "ca_synthetic_inspection"

/** True ONLY for the automated funnel-inspection browser (which sets
 * SYNTHETIC_BOT_KEY before any navigation). Every real device returns false and
 * is recorded normally. Fails OPEN (false) if storage is unavailable. */
export function isSyntheticInspectionBot(): boolean {
  if (typeof window === "undefined") return false
  try {
    return localStorage.getItem(SYNTHETIC_BOT_KEY) === "1"
  } catch {
    return false
  }
}

// Our OWN internal traffic (owner + Matthew devices) is excluded from PostHog
// so our testing never pollutes the tiny real funnel data (owner, 2026-07-18:
// "we should ideally take our own internal traffic out actually" - reverses
// the 2026-07-17 record-everything stance for OUR OWN devices only; every real
// visitor is still recorded). A device opts itself out for good by visiting
// once with ?ca_internal=1 (and can opt back in with ?ca_internal=0). Simple,
// per-device, no PostHog-side config; instrumentation-client reads this BEFORE
// deciding whether to init PostHog, so an internal device sends zero calls.
const INTERNAL_TRAFFIC_KEY = "ca_internal_traffic"

/** Persist / clear the internal-traffic opt-out from the URL. Call before the
 * isInternalTrafficDevice() check so ?ca_internal=1 takes effect on this very
 * page load. Idempotent; fails soft when storage is unavailable. */
export function captureInternalTrafficFlag(): void {
  if (typeof window === "undefined") return
  try {
    const flag = new URLSearchParams(window.location.search).get("ca_internal")
    if (flag === "1") localStorage.setItem(INTERNAL_TRAFFIC_KEY, "1")
    else if (flag === "0") localStorage.removeItem(INTERNAL_TRAFFIC_KEY)
  } catch {
    // storage unavailable — nothing to persist
  }
}

/** True for a device the owner marked internal (via ?ca_internal=1). Every real
 * visitor returns false and is recorded normally. Fails OPEN (false). */
export function isInternalTrafficDevice(): boolean {
  if (typeof window === "undefined") return false
  try {
    return localStorage.getItem(INTERNAL_TRAFFIC_KEY) === "1"
  } catch {
    return false
  }
}

// ---------------------------------------------------------------------------
// Situation pages (web/lib/situation-page-registry.ts): the PostHog half of
// situation measurement. The durable half, the fields the apply route and the
// embedded checkout store, lives in web/lib/situation-analytics.ts, which is
// pure and imports the registry. THIS file must stay registry-free: it is
// bundled into every page through the intro popup and the newsletter form,
// and the registry plus the commercial map is not something every page should
// carry. So every helper here takes a SituationEventContext the page's server
// component built once with situationEventContext(slug) and handed down.
// ---------------------------------------------------------------------------

/** The registry-free context every situation event is stamped with. Built
 * server-side by situationEventContext() in web/lib/situation-analytics.ts. */
export type SituationEventContext = {
  /** The slug, e.g. "breakup-coaching". */
  situation_id: string
  /** The registry cluster, e.g. "relationships_and_transitions". */
  situation_cluster: string
  /** The page's own path, e.g. "/breakup-coaching". */
  page_path: string
}

/** The query parameter a situation page's CTA may carry into /apply or the
 * checkout. The same literal is SITUATION_QUERY_PARAM in
 * web/lib/situation-analytics.ts, which cannot be imported here without
 * pulling the registry into every page; the eval pins the two equal. */
export const SITUATION_QUERY_PARAM = "situation"

const LAST_COMMERCIAL_PATH_KEY = "ca_last_commercial_path"

/**
 * Record the commercial page he is standing on: a localStorage mirror for the
 * event props, and the PostHog person property `last_commercial_path`.
 *
 * The SAME shape as first touch, on purpose, and the opposite write rule.
 * captureFirstTouch() keeps the first value it ever sees and never overwrites
 * it; this keeps the LAST, because the question it answers is which commercial
 * page a man touched most recently before he applied, booked or paid. So it is
 * a plain `$set` (setPersonProperties with the properties as the first
 * argument), never `$set_once`, and every situation page view moves it.
 */
export function recordLastCommercialPath(path: string | null | undefined): void {
  if (typeof window === "undefined") return
  const value = (path ?? "").trim().slice(0, 200)
  if (!value.startsWith("/")) return
  try {
    localStorage.setItem(LAST_COMMERCIAL_PATH_KEY, value)
  } catch {
    // Storage unavailable. The person property below still carries it.
  }
  try {
    posthog.setPersonProperties({ last_commercial_path: value })
  } catch {
    // Analytics is never allowed to break a page.
  }
}

/** The most recent commercial page this browser recorded, or undefined. */
export function lastCommercialPath(): string | undefined {
  if (typeof window === "undefined") return undefined
  try {
    return localStorage.getItem(LAST_COMMERCIAL_PATH_KEY) || undefined
  } catch {
    return undefined
  }
}

/** The path this browser first landed on, read out of the stored first-touch
 * summary (`… | landed: /path`), so first and last touch come from ONE record
 * rather than a second mechanism. Undefined when nothing was recorded. */
export function firstTouchPath(): string | undefined {
  const summary = firstTouchSummary()
  const match = /(?:^|\| )landed: (\/\S*)/.exec(summary)
  return match ? match[1].slice(0, 200) : undefined
}

/** The same-origin article this page was reached from, when the referrer is
 * one of ours under /articles/. Undefined for anything else, including a
 * cross-origin referrer, which is first-touch information PostHog already has. */
export function articleOrigin(): string | undefined {
  if (typeof window === "undefined") return undefined
  try {
    if (!document.referrer) return undefined
    const url = new URL(document.referrer)
    if (url.hostname !== window.location.hostname) return undefined
    if (!url.pathname.startsWith("/articles/")) return undefined
    return url.pathname.slice(0, 200)
  } catch {
    return undefined
  }
}

/** The `?situation=` a CTA carried onto the current page, or undefined. Sent
 * to the apply route and the checkout route beside `entry_page`, and validated
 * there against the registry; the browser never decides what it means. */
export function situationQueryFromUrl(): string | undefined {
  if (typeof window === "undefined") return undefined
  try {
    const value = new URLSearchParams(window.location.search).get(SITUATION_QUERY_PARAM)
    return value?.trim() ? value.trim().slice(0, 80) : undefined
  } catch {
    return undefined
  }
}

/**
 * The props every situation event carries: the context, plus first touch, last
 * commercial path and article origin where the browser knows them. Each of the
 * optional three is OMITTED when unknown rather than sent as null, so a
 * breakdown never grows a phantom "null" cohort beside the real values.
 */
export function situationEventProps(
  context: SituationEventContext,
  extra?: AnalyticsProps,
): AnalyticsProps {
  const first = firstTouchPath()
  const last = lastCommercialPath()
  const article = articleOrigin()
  return {
    situation_id: context.situation_id,
    situation_cluster: context.situation_cluster,
    page_path: context.page_path,
    ...(first ? { first_touch_path: first } : {}),
    ...(last ? { last_commercial_path: last } : {}),
    ...(article ? { article_origin: article } : {}),
    ...extra,
  }
}

/**
 * Fire `situation_page_viewed` once per mount and move `last_commercial_path`
 * to this page. Call it from one client component the page renders; the
 * server component builds the context with situationEventContext(slug).
 *
 * The record runs BEFORE the event so the event's own `last_commercial_path`
 * agrees with the person property at that moment; a later CTA click then
 * reads the same value. Guarded on a ref so React's development double-invoke
 * of effects fires it once.
 */
export function useSituationPageView(context: SituationEventContext): void {
  const fired = useRef<string | null>(null)
  useEffect(() => {
    if (fired.current === context.page_path) return
    fired.current = context.page_path
    recordLastCommercialPath(context.page_path)
    track(AnalyticsEvent.SituationPageViewed, situationEventProps(context))
  }, [context])
}

/** He chose one of the page's first-question paths. `path_id` is the option's
 * stable id; `recommended_product` is the product path the choice routes to,
 * e.g. "/mentorship", when the choice names one. */
export function trackSituationPathSelected(
  context: SituationEventContext,
  detail: {
    path_id: string
    path_label?: string
    recommended_product?: string
    cta_module?: string
  },
): void {
  track(AnalyticsEvent.SituationPathSelected, situationEventProps(context, detail))
}

/** He clicked a related guide. `guide_kind` says whether the link went to a
 * sibling situation page, an article, or an existing offer. */
export function trackRelatedGuideClicked(
  context: SituationEventContext,
  detail: {
    href: string
    guide_kind?: "situation" | "article" | "offer"
    cta_module?: string
  },
): void {
  track(AnalyticsEvent.RelatedGuideClicked, situationEventProps(context, detail))
}

/** A recommended product card was shown. `recommended_product` is the product
 * path, e.g. "/ai-photos"; `diagnosis` is the router branch that surfaced it. */
export function trackRecommendedProductViewed(
  context: SituationEventContext,
  detail: { recommended_product: string; diagnosis?: string; cta_module?: string },
): void {
  track(AnalyticsEvent.RecommendedProductViewed, situationEventProps(context, detail))
}

/** He clicked a CTA. `cta_module` names the section the button sits in;
 * `href` is where it sent him; `recommended_product` is the product path when
 * the CTA leads to one. */
export function trackSituationCtaClicked(
  context: SituationEventContext,
  detail: { cta_module: string; href: string; recommended_product?: string },
): void {
  track(AnalyticsEvent.SituationCtaClicked, situationEventProps(context, detail))
}
